import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { KayakIntegrationService, KayakSearchResponse, KayakDeal } from '../../services/kayak-integration.service';

@Component({
  selector: 'app-kayak-integration',
  templateUrl: './kayak-integration.component.html',
  styleUrls: ['./kayak-integration.component.scss']
})
export class KayakIntegrationComponent implements OnInit {
  isLoading = false;
  errorMessage = '';
  responseNote = '';
  deals: KayakDeal[] = [];

  searchForm = this.formBuilder.group({
    origin: ['SFO', Validators.required],
    destination: ['JFK', Validators.required],
    departDate: ['2025-01-22', Validators.required],
    returnDate: ['2025-01-28'],
    adults: [1, [Validators.required, Validators.min(1)]]
  });

  constructor(
    private formBuilder: FormBuilder,
    private kayakIntegration: KayakIntegrationService
  ) {}

  ngOnInit(): void {
    this.fetchDeals();
  }

  fetchDeals(): void {
    if (this.searchForm.invalid) {
      this.searchForm.markAllAsTouched();
      return;
    }

    this.errorMessage = '';
    this.responseNote = '';
    this.isLoading = true;

    this.kayakIntegration
      .searchFlights(this.searchForm.getRawValue())
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: KayakSearchResponse) => {
          this.deals = response.results ?? [];
          this.responseNote = response.note || 'Live pricing from the KAYAK partner API.';
        },
        error: () => {
          this.errorMessage = 'We could not reach the KAYAK integration service. Please try again in a moment.';
          this.deals = [];
        }
      });
  }

  openKayak(deal: KayakDeal): void {
    const url = deal.deepLink || 'https://www.kayak.com';
    window.open(url, '_blank', 'noopener');
  }

  downloadCalendar(deal: KayakDeal): void {
    const title = `Flight: ${deal.route}`;
    const description = `Booked via KAYAK. Price: ${deal.price}`;
    const startDate = deal.departDate || this.searchForm.value.departDate || '';
    const endDate = deal.returnDate || this.searchForm.value.returnDate || startDate;

    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Clarichat//Kayak Integration//EN',
      'BEGIN:VEVENT',
      `SUMMARY:${title}`,
      `DESCRIPTION:${description}`,
      `DTSTART:${this.formatIcsDate(startDate)}`,
      `DTEND:${this.formatIcsDate(endDate)}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\n');

    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'kayak-trip.ics';
    link.click();
    window.URL.revokeObjectURL(link.href);
  }

  getTripsEmailLink(deal: KayakDeal): string {
    const subject = encodeURIComponent(`KAYAK Trip: ${deal.route}`);
    const body = encodeURIComponent(
      `Please add this itinerary to my KAYAK Trips.\n\n` +
        `Route: ${deal.route}\n` +
        `Airline: ${deal.airline || 'TBD'}\n` +
        `Depart: ${deal.departDate}\n` +
        `Return: ${deal.returnDate || 'One-way'}\n` +
        `Price: ${deal.price}\n` +
        `Deep link: ${deal.deepLink || 'https://www.kayak.com'}\n`
    );

    return `mailto:trips@kayak.com?subject=${subject}&body=${body}`;
  }

  private formatIcsDate(date: string): string {
    const sanitized = date.replace(/-/g, '');
    return `${sanitized}T090000Z`;
  }
}
